import { Component, OnDestroy, OnInit } from '@angular/core';
import { RoleLookup, User, UserNavigation, UserNavigationItem } from '@common/entities';
import { Input } from '@angular/core';
import {
  AuthenticationMethodLookup,
  AuthenticationMethods,
  AuthenticationService,
  IdpDataAlbertaCa,
} from '@govalta-emu/keycloak-auth-service';
import { ContextProvider } from '../ui-shared-components.services';
import { GoabMenuButtonOnActionDetail } from '@abgov/ui-components-common';
import { Utilities } from '@common/utils';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Lookup } from '@common/constants';

@Component({
  standalone: false,
  selector: 'common-header-navigation',
  styleUrls: ['./header-navigation.component.scss'],
  templateUrl: `./header-navigation.component.html`,
})
export class HeaderNavigationComponent implements OnInit, OnDestroy {
  constructor(
    private authenticationService: AuthenticationService,
    private contextProvider: ContextProvider,
    private router: Router
  ) {}
  // @Input() UserNavigation: UserNavigation;
  @Input() headerTitle: string;
  @Input() fallbackUserName: string; //allows consumer to pass in a fallback user name if not logged in with alberta.ca account.
  @Input() testRole: RoleLookup; //consumer can specify testRole to prohibit access to change password for testers.
  @Input() isMobile: boolean | undefined; //consumer can pick mobile or desktop mode.
  @Input() albertaCaAccountUrl: string | undefined; //consumer can specify alberta.ca account url if needed for dev/test/uat/prod.
  @Input() hideBadgeStatus: boolean;
  @Input() hidePrimaryRole: boolean;
  @Input() secondaryText = '';
  @Input() loginIdp: string | undefined = AuthenticationMethods.albertaCa.idpValue;

  isAuthenticated: boolean | undefined;
  authenticationMethod: AuthenticationMethodLookup;
  profileFromToken: User;
  primaryRole: string;
  isTestUser: boolean;
  _destroy$ = new Subject<void>();
  badgeContentStatus = '';
  badgeTypeStatus = '' as any;
  userProfile: IdpDataAlbertaCa;

  private _userNavigation: UserNavigation | undefined;
  @Input() set userNavigation(value) {
    this._userNavigation = value;
    if (value && !this._userNavigation) {
      this._userNavigation = value;
    }
  }
  get userNavigation() {
    return this._userNavigation;
  }

  private _albertaCaFullName: string | undefined;
  get profileName() {
    if (this._albertaCaFullName) {
      return this._albertaCaFullName;
    }
    if (this.userProfile && !this._albertaCaFullName) {
      this.setBadge();
      this._albertaCaFullName = Utilities.getClientName({
        firstName: this.userProfile.firstName,
        lastName: this.userProfile.lastName,
      });
      return this._albertaCaFullName;
    }
    return this.fallbackUserName;
  }

  get initials() {
    if (this.profileName) {
      return this.profileName
        .split(' ')
        .map((n) => n[0])
        .join('');
    }
    return '';
  }

  async ngOnInit() {
    this.isAuthenticated = this.authenticationService.isAuthenticated();
    this.authenticationMethod = this.authenticationService.authenticationMethod();
    this.loadTokenInfo();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  setBadge() {
    if (this.userProfile?.verified) {
      this.badgeContentStatus = 'Verified';
      this.badgeTypeStatus = 'success';
    } else {
      this.badgeContentStatus = 'Basic';
      this.badgeTypeStatus = 'important';
    }
    if (this.authenticationMethod.code === AuthenticationMethods.keycloak.code && !this.isTestUser) {
      this.badgeTypeStatus = 'default'; //if not logged in with keycloak, use grey badge so we know
    }
  }

  //gets information from auth token
  //will also set fallbackUserName if not already set by consumer.
  loadTokenInfo() {
    if (this.contextProvider) {
      const currentUser = this.contextProvider.getCurrentUser();
      currentUser.pipe(takeUntil(this._destroy$)).subscribe((data) => {
        if (!this.profileFromToken) {
          this.profileFromToken = data;
          this.primaryRole = this.profileFromToken?.primaryRole;
          // if (this.primaryRole && !this.secondaryText && !this.hidePrimaryRole) {
          //   this.secondaryText = `${this.primaryRole}`;
          // }
          this.isTestUser = this.isInRoles(this.profileFromToken, [this.testRole]);
          if (!this.fallbackUserName) {
            this.fallbackUserName = this.profileFromToken?.name;
            if (!this.fallbackUserName) {
              this.fallbackUserName = this.profileFromToken?.email;
            }
          }
          this.setBadge();
          this.loadAlbertaCaInfo(); //asyncronously load alberta.ca info once we've got here
        }
      });
    }
  }

  //only loads alberta.ca profile if they are logged in with alberta.ca or if it's a test user mimicing alberta.ca login
  async loadAlbertaCaInfo() {
    const validateIdentity = !this.isTestUser; //by pass identity validation if test user
    if (
      this.profileFromToken &&
      (this.authenticationMethod.code === AuthenticationMethods.albertaCa.code || this.isTestUser)
    ) {
      //if user info in token, then try get clean alberta.ca data
      this.userProfile = await this.authenticationService.getIdpDataAlbertaCa(validateIdentity);
    }
  }

  login() {
    const returnUrl = '/dash?login=true';
    this.contextProvider.setCurrentUser(null);
    this.authenticationService.signIn(returnUrl, this.loginIdp);
  }

  logout(url?: string) {
    //remove reference to created session application for the auth user
    const returnUrl = url ?? '/';
    this.contextProvider.setCurrentUser(null);
    this.authenticationService.signOut(returnUrl);
  }
  changePassword() {
    this.authenticationService.changePassword();
  }

  openInNewTab(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  isInRoles(profile: User, roles: Array<Lookup>) {
    if (roles && profile?.roles) {
      const intersection = roles.filter((role) => profile?.roles.includes(role.code));
      if (intersection.length > 0) {
        return true;
      }
    }
    return false;
  }

  menuAction(detail: GoabMenuButtonOnActionDetail) {
    const navigationItem = this._userNavigation.menuItems.find((i) => i.action === detail.action);

    if (!navigationItem) {
      return;
    }

    if (Utilities.hasValue(navigationItem.url)) {
      if (navigationItem.newWindow) {
        this.openInNewTab(navigationItem.url);
      } else {
        this.router.navigateByUrl(navigationItem.url);
      }
      return;
    }

    switch (navigationItem.action) {
      case 'signOut': {
        let redirectUrl = this._userNavigation.logoutUrl ?? '/';
        if (this.isTestUser) {
          redirectUrl = this._userNavigation.partnerLogoutUrl ?? '/';
        }
        this.logout(redirectUrl);
        break;
      }
      case 'changePassword':
        this.changePassword();
        break;
      case 'albertaCaAccountRedirect':
        if (Utilities.hasValue(this.albertaCaAccountUrl)) {
          this.openInNewTab(this.albertaCaAccountUrl);
        }
        break;
    }
  }

  showMenuItem(menuItem: UserNavigationItem) {
    // console.log(menuItem);
    let showMenu = true;
    //run checks to toggle off showMenu
    if (menuItem.roleAccess?.length) {
      if (!this.isInRoles(this.profileFromToken, menuItem.roleAccess)) {
        showMenu = false;
      }
    }
    if (showMenu && menuItem.albertaCaOnly) {
      //only run this check if they are logged in with alberta.ca account
      if (this.authenticationMethod.code !== AuthenticationMethods.albertaCa.code) {
        showMenu = false;
      }
      if (!Utilities.hasValue(this.albertaCaAccountUrl) && menuItem.action === 'albertaCaAccountRedirect') {
        showMenu = false;
      }
    }
    if (showMenu && menuItem.keycloakOnly) {
      //only run this check if they are logged in with keycloak account
      if (this.authenticationMethod.code !== AuthenticationMethods.keycloak.code) {
        showMenu = false;
      }
      if (menuItem.action === 'changePassword' && this.isTestUser) {
        //do not allow test user to change password
        showMenu = false;
      }
    }
    if (showMenu && menuItem.goaOnly) {
      const email = this.profileFromToken?.email;
      if (!email?.endsWith('@gov.ab.ca')) {
        showMenu = false;
      }
    }
    return showMenu;
  }
}
